<template>
  <div class="row">
    <div class="col-md-12">
      <!-- Access Denied Message -->
      <div v-if="!hasCouponAccess" class="access-denied-container">
        <div class="access-denied-card">
          <div class="access-denied-icon">🔒</div>
          <h2>Access Denied</h2>
          <p>You don't have permission to access the coupon upload feature.</p>
          <p class="access-denied-hint">Please contact your administrator to request access.</p>
          <button class="btn btn-primary" @click="goHome">Go to Home</button>
        </div>
      </div>

      <!-- Coupon Upload Form (only shown if user has access) -->
      <template v-else>
        <h1 class="text-center">Upload Coupon</h1>
        <div class="alert alert-info mb-2" role="alert" style="max-width: 600px; margin: auto">
          Add coupon promotions to marketing tables. Fill in all fields and submit. The value will be added to the cost as negative.
        </div>
        <div class="card text-center mb-4" style="max-width: 600px; margin: auto">
          <div class="card-header text-center"></div>
          <div class="card-body">
            <!-- App Dropdown -->
            <div class="form-group">
              <label>App</label>
              <select class="form-control" v-model="app">
                <option value="" disabled>Select an app</option>
                <option v-for="appName in apps" :key="appName" :value="appName">
                  {{ appName }}
                </option>
              </select>
            </div>

            <!-- Date Range -->
            <div class="form-group">
              <label>Date Range</label>
              <div class="date-range">
                <div class="form-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    class="form-control"
                    v-model="startDate"
                    :max="maxDate"
                  />
                </div>
                <div class="form-group">
                  <label>End Date</label>
                  <input
                    type="date"
                    class="form-control"
                    v-model="endDate"
                    :min="startDate"
                    :max="maxDate"
                  />
                </div>
              </div>
            </div>

            <!-- Country (temporarily disabled)
            <div class="form-group">
              <label>Country</label>
              <input
                type="text"
                class="form-control"
                v-model="country"
                placeholder="e.g. US, DE, TR"
              />
            </div>
            -->

            <!-- OS (temporarily disabled)
            <div class="form-group">
              <label>OS</label>
              <input
                type="text"
                class="form-control"
                v-model="os"
                placeholder="e.g. ios, android"
              />
            </div>
            -->

            <!-- Platform -->
            <div class="form-group">
              <label>Platform</label>
              <select class="form-control" v-model="platform">
                <option value="" disabled>Select a platform</option>
                <option v-for="p in platforms" :key="p" :value="p">
                  {{ p }}
                </option>
              </select>
            </div>

            <!-- Coupon Name -->
            <div class="form-group">
              <label>Coupon Name</label>
              <input
                type="text"
                class="form-control"
                v-model="couponName"
                placeholder="Enter coupon name"
              />
            </div>

            <!-- Value -->
            <div class="form-group">
              <label>Value (Cost)</label>
              <input
                type="number"
                step="0.01"
                class="form-control"
                v-model.number="value"
                placeholder="e.g. 100.50"
              />
            </div>
          </div>
          <div class="card-footer">
            <button
              class="btn btn-primary"
              @click="showConfirmation"
              :disabled="state !== 'waiting' || !isFormValid"
            >
              {{ buttonText }}
            </button>
          </div>
        </div>

        <!-- Confirmation Modal -->
        <div class="modal-overlay" v-if="showModal" @click.self="closeModal">
          <div class="modal-content">
            <h4>Confirm Coupon Upload</h4>
            <div class="confirmation-details">
              <p>
                <strong>{{ value }}</strong> will be split between the dates <strong>{{ startDate }}</strong> and <strong>{{ endDate }}</strong> 
                and will be inserted into <strong>{{ app }}</strong> marketing table with the tracker:
              </p>
              <ul class="tracker-details">
                <li><strong>platform:</strong> {{ platform }}</li>
                <li><strong>campaign:</strong> {{ platform }}-coupons</li>
                <li><strong>adgroup:</strong> {{ couponName }}</li>
              </ul>
              <p class="confirm-question">Do you want to proceed?</p>
            </div>
            <div class="modal-actions">
              <button class="btn btn-secondary" @click="closeModal">Cancel</button>
              <button class="btn btn-primary" @click="submitCoupon">Confirm</button>
            </div>
          </div>
        </div>

        <!-- Sheet Load Confirmation Modal -->
        <div class="modal-overlay" v-if="showSheetModal" @click.self="closeSheetModal">
          <div class="modal-content">
            <h4>Load from Sheet</h4>
            <div class="confirmation-details">
              <p>This will load the ready rows from sheet, you want to proceed?</p>
            </div>
            <div class="modal-actions">
              <button class="btn btn-secondary" @click="closeSheetModal">Cancel</button>
              <button class="btn btn-primary" @click="loadFromSheet">Confirm</button>
            </div>
          </div>
        </div>

        <!-- Load from Sheet Button -->
        <button
          class="btn btn-secondary load-sheet-btn"
          @click="showSheetModal = true"
          :disabled="sheetLoadState === 'loading'"
        >
          {{ sheetLoadState === 'loading' ? 'Loading...' : 'Load from sheet' }}
        </button>
      </template>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import notify from "../util/notification";
import tokenManagerService from "../services/tokenManager";

const dateObjToString = (dateObj) => {
  let dd = dateObj.getDate();
  if (dd < 10) dd = "0" + dd;
  let mm = dateObj.getMonth() + 1;
  if (mm < 10) mm = "0" + mm;
  let yyyy = dateObj.getFullYear();
  return yyyy + "-" + mm + "-" + dd;
};

const isLocal = window.location.href.indexOf("localhost") >= 0;
const backendUrl = isLocal ? "http://localhost:5002" : "";

export default {
  name: 'UploadCouponPage',
  data() {
    return {
      app: "",
      startDate: "",
      endDate: "",
      // country: "", // temporarily disabled
      // os: "", // temporarily disabled
      platform: "",
      couponName: "",
      value: null,
      state: "waiting",
      showModal: false,
      showSheetModal: false,
      sheetLoadState: "idle",
    };
  },
  computed: {
    hasCouponAccess() {
      return this.$store.getters.hasCouponAccess;
    },
    apps() {
      return this.$store.getters.appTableNames;
    },
    platforms() {
      return this.$store.getters.platforms;
    },
    maxDate() {
      return dateObjToString(new Date());
    },
    isFormValid() {
      return (
        this.app &&
        this.startDate &&
        this.endDate &&
        // this.country.trim() && // temporarily disabled
        // this.os.trim() && // temporarily disabled
        this.platform.trim() &&
        this.couponName.trim() &&
        this.value !== null &&
        this.value !== "" &&
        !isNaN(this.value)
      );
    },
    buttonText() {
      if (this.state === "waiting") return "Add Coupon";
      if (this.state === "submitting") return "Submitting...";
      if (this.state === "submitted") return "Submitted";
      return "Add Coupon";
    },
  },
  methods: {
    goHome() {
      this.$router.push('/');
    },
    showConfirmation() {
      if (!this.isFormValid) {
        notify("Please fill in all fields!", "error");
        return;
      }

      if (new Date(this.startDate) > new Date(this.endDate)) {
        notify("Start date cannot be after end date!", "error");
        return;
      }

      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },
    async submitCoupon() {
      this.showModal = false;
      this.state = "submitting";

      try {
        const token = await tokenManagerService.getValidToken();

        const response = await axios.post(
          `${backendUrl}/upload-coupon`,
          {
            app_name: this.app,
            // os_name: this.os, // temporarily disabled
            // country: this.country, // temporarily disabled
            platform: this.platform,
            start_date: this.startDate,
            end_date: this.endDate,
            cost: this.value,
            coupon_name: this.couponName,
          },
          {
            headers: {
              authorization: `Bearer ${token}`,
            },
          }
        );

        notify("Coupon uploaded successfully!", "success");
        this.state = "submitted";
        this.resetForm();
      } catch (error) {
        console.error('Submit coupon error:', error);
        notify(`Error: ${error.response?.data?.message || error.message}`, "error");
        this.state = "waiting";
      }
    },
    resetForm() {
      setTimeout(() => {
        this.app = "";
        this.startDate = "";
        this.endDate = "";
        // this.country = ""; // temporarily disabled
        // this.os = ""; // temporarily disabled
        this.platform = "";
        this.couponName = "";
        this.value = null;
        this.state = "waiting";
      }, 1500);
    },
    closeSheetModal() {
      this.showSheetModal = false;
    },
    async loadFromSheet() {
      this.showSheetModal = false;
      this.sheetLoadState = "loading";

      try {
        const token = await tokenManagerService.getValidToken();

        await axios.post(
          `${backendUrl}/upload-coupon`,
          {
            source: "sheet",
          },
          {
            headers: {
              authorization: `Bearer ${token}`,
            },
          }
        );

        notify("Successfull! Check the sheet for the results.", "success");
        this.sheetLoadState = "idle";
      } catch (error) {
        console.error("Load from sheet error:", error);
        notify(`Error: ${error.response?.data?.message || error.message}`, "error");
        this.sheetLoadState = "idle";
      }
    },
  },
};
</script>

<style scoped>
.access-denied-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
}

.access-denied-card {
  background: #1e1e2f;
  border: 1px solid #495057;
  border-radius: 12px;
  padding: 3rem;
  text-align: center;
  max-width: 450px;
}

.access-denied-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.access-denied-card h2 {
  color: #dc3545;
  margin-bottom: 1rem;
}

.access-denied-card p {
  color: #adb5bd;
  margin-bottom: 0.5rem;
}

.access-denied-hint {
  font-size: 0.9rem;
  color: #6c757d;
  margin-bottom: 1.5rem;
}

.date-range {
  display: flex;
  gap: 1rem;
}

.date-range .form-group {
  flex: 1;
}

input[type="date"],
input[type="text"],
input[type="number"],
select {
  background-color: #343a40;
  color: white;
  border-color: #495057;
}

input[type="date"]::-webkit-calendar-picker-indicator {
  filter: invert(1);
  cursor: pointer;
}

select {
  cursor: pointer;
}

select option {
  background-color: #343a40;
  color: white;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: #1e1e2f;
  padding: 2rem;
  border-radius: 8px;
  max-width: 500px;
  width: 90%;
  color: white;
  border: 1px solid #495057;
}

.modal-content h4 {
  margin-bottom: 1.5rem;
  text-align: center;
}

.confirmation-details {
  background: #343a40;
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1.5rem;
}

.confirmation-details p {
  margin: 0.5rem 0;
  text-align: left;
}

.tracker-details {
  margin: 1rem 0;
  padding-left: 1.5rem;
  text-align: left;
}

.tracker-details li {
  margin: 0.3rem 0;
}

.confirm-question {
  margin-top: 1rem;
  font-style: italic;
}

.modal-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
}

label {
  text-align: left;
  display: block;
}

.load-sheet-btn {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
}
</style>
